import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../api';


const SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'];
const COLORS = ['Black', 'Off White', 'Blue', 'Maroon', 'Beige', 'Pink', 'Matcha', 'Mocha'];

function stockoutBadge(days, qty) {
  if (qty <= 0) return { label: 'Stockout', class: 'badge-red' };
  // no sales in the window => no burn rate, so it can't run out. Show that
  // rather than a dash, so the column reads top-to-bottom with no gaps.
  if (days == null) return { label: 'No sales', class: 'badge-green' };
  if (days <= 10) return { label: `${days}d ⚠`, class: 'badge-red' };
  if (days <= 20) return { label: `${days}d`, class: 'badge-yellow' };
  return { label: `${days}d`, class: 'badge-green' };
}

export default function Blanks() {
  const [tab, setTab] = useState('shelf'); // 'shelf' | 'incoming' | 'reorder'
  const [blanks, setBlanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [form, setForm] = useState({ color: 'Black', size: 'M', quantity: '' });
  const [pendingReview, setPendingReview] = useState([]);
  const [confirmingId, setConfirmingId] = useState(null);
  const [confirmForm, setConfirmForm] = useState({});
  const [adjustingCell, setAdjustingCell] = useState(null);

  const [incoming, setIncoming] = useState([]);
  const [incForm, setIncForm] = useState({ color: 'Black', size: 'M', quantity: '', note: '' });
  const [addingInc, setAddingInc] = useState(false);
  const [receivingId, setReceivingId] = useState(null);

  const [analytics, setAnalytics] = useState(null);
  // Raw box text, so clearing a box to retype doesn't snap it back to 30
  const [coverInput, setCoverInput] = useState('30');
  const [windowInput, setWindowInput] = useState('30');
  const coverDays = parseInt(coverInput) || 30;
  const windowDays = parseInt(windowInput) || 30;
  const [onlyReorder, setOnlyReorder] = useState(true);

  useEffect(() => { fetchBlanks(); fetchPendingReview(); fetchIncoming(); }, []);
  // Wait for typing to pause, and drop any answer that arrives after the
  // numbers changed. Without this, typing "60" also asked for "6", and if
  // that slower answer landed last the table showed 6 days of cover.
  useEffect(() => {
    if (tab !== 'reorder') return;
    let stale = false;
    const t = setTimeout(async () => {
      try {
        const data = (await axios.get(`${API}/api/blanks/analytics?coverDays=${coverDays}&windowDays=${windowDays}`)).data;
        if (!stale) setAnalytics(data);
      } catch (e) { console.error(e); }
    }, 400);
    return () => { stale = true; clearTimeout(t); };
  }, [tab, coverDays, windowDays]);

  const fetchBlanks = async () => {
    try { setBlanks((await axios.get(`${API}/api/blanks`)).data); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  };
  const fetchPendingReview = async () => {
    try { setPendingReview((await axios.get(`${API}/api/blanks/pending-review`)).data); } catch (e) { console.error(e); }
  };
  const fetchIncoming = async () => {
    try { setIncoming((await axios.get(`${API}/api/blanks/incoming`)).data); } catch (e) { console.error(e); }
  };

  const getQty = (color, size) => {
    const item = blanks.find(b => b.color === color && b.size === size);
    return item ? item.quantity : 0;
  };

  const handleQuickAdjust = async (color, size, currentQty) => {
    const val = window.prompt(`${color} ${size} blank — current stock: ${currentQty}\n\nHow many to add? (negative subtracts)`);
    if (val === null || val.trim() === '') return;
    const qty = parseInt(val, 10);
    if (isNaN(qty) || qty === 0) { alert('Please enter a number'); return; }
    setAdjustingCell(`${color}-${size}`);
    try { await axios.post(`${API}/api/blanks/update`, { color, size, quantity: qty }); await fetchBlanks(); }
    catch (e) { alert('Update failed: ' + (e.response?.data?.message || e.message)); }
    finally { setAdjustingCell(null); }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setUpdating(true);
    try {
      await axios.post(`${API}/api/blanks/set`, { color: form.color, size: form.size, quantity: parseInt(form.quantity) });
      await fetchBlanks();
      setForm({ ...form, quantity: '' });
    } catch (e) { console.error(e); } finally { setUpdating(false); }
  };

  const updateConfirmForm = (id, field, value) => setConfirmForm(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  const handleConfirmBlank = async (id) => {
    const data = confirmForm[id] || {};
    setConfirmingId(id);
    try {
      await axios.post(`${API}/api/blanks/confirm`, {
        id, productType: data.productType || 'Oversized Tee 240GSM',
        quantity: data.quantity !== undefined ? parseInt(data.quantity) : 0
      });
      await fetchBlanks(); await fetchPendingReview();
    } catch (e) { alert('Confirm failed'); } finally { setConfirmingId(null); }
  };

  const handleAddIncoming = async (e) => {
    e.preventDefault();
    if (!incForm.quantity) return;
    setAddingInc(true);
    try {
      await axios.post(`${API}/api/blanks/incoming/add`, { ...incForm, quantity: parseInt(incForm.quantity) });
      setIncForm({ ...incForm, quantity: '', note: '' });
      await fetchIncoming();
    } catch (e) { alert('Failed to add incoming: ' + (e.response?.data?.message || e.message)); }
    finally { setAddingInc(false); }
  };
  const handleReceive = async (id) => {
    setReceivingId(id);
    try { const r = await axios.post(`${API}/api/blanks/incoming/receive/${id}`); alert('✓ ' + r.data.message); await fetchIncoming(); await fetchBlanks(); }
    catch (e) { alert('Receive failed: ' + (e.response?.data?.message || e.message)); }
    finally { setReceivingId(null); }
  };
  const handleDeleteIncoming = async (id) => {
    if (!window.confirm('Remove this incoming entry?')) return;
    try { await axios.delete(`${API}/api/blanks/incoming/${id}`); await fetchIncoming(); } catch (e) { alert('Delete failed'); }
  };

  const totalUnits = blanks.reduce((sum, b) => sum + b.quantity, 0);

  const Tab = ({ id, label }) => (
    <button onClick={() => setTab(id)} style={{
      padding: '9px 18px', fontSize: '14px', borderRadius: '8px', cursor: 'pointer',
      background: tab === id ? '#6e1f2a' : '#ffffff', color: tab === id ? '#fff' : '#7d6a63',
      border: `1px solid ${tab === id ? '#6e1f2a' : '#e6dccc'}`
    }}>{label}</button>
  );

  return (
    <div>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <Tab id="shelf" label="On Shelf" />
        <Tab id="incoming" label={`Incoming Stock (${incoming.length})`} />
        <Tab id="reorder" label="Reorder Plan" />
      </div>

      {/* ---------------- TAB 1: ON SHELF ---------------- */}
      {tab === 'shelf' && (<>
        <div className="stat-grid">
          <div className="stat-card"><div className="stat-label">Total Blank Units</div><div className="stat-value">{totalUnits}</div></div>
          <div className="stat-card"><div className="stat-label">Colours</div><div className="stat-value">{COLORS.length}</div></div>
          <div className="stat-card"><div className="stat-label">Incoming Units</div><div className="stat-value" style={{ color: '#8c3242' }}>{incoming.reduce((s, i) => s + i.quantity, 0)}</div></div>
        </div>

        {pendingReview.length > 0 && (
          <div className="card">
            <div className="card-title">⚠ New Colours/Products Pending Review</div>
            <table>
              <thead><tr><th>Colour</th><th>Size</th><th>Product Type</th><th>Actual Qty</th><th>Action</th></tr></thead>
              <tbody>
                {pendingReview.map(p => (
                  <tr key={p._id}>
                    <td style={{ fontWeight: 600 }}>{p.color}</td>
                    <td>{p.size}</td>
                    <td>
                      <select value={confirmForm[p._id]?.productType ?? 'Oversized Tee 240GSM'} onChange={e => updateConfirmForm(p._id, 'productType', e.target.value)} style={{ width: '200px' }}>
                        <option value="Oversized Tee 240GSM">Oversized Tee 240GSM</option>
                        <option value="Cargo Shorts">Cargo Shorts</option>
                        <option value="Other">Other</option>
                      </select>
                    </td>
                    <td><input type="number" placeholder="0" value={confirmForm[p._id]?.quantity ?? ''} onChange={e => updateConfirmForm(p._id, 'quantity', e.target.value)} style={{ width: '80px' }} /></td>
                    <td><button className="btn-primary" onClick={() => handleConfirmBlank(p._id)} disabled={confirmingId === p._id} style={{ fontSize: '12px', padding: '6px 12px' }}>{confirmingId === p._id ? 'Saving...' : 'Confirm'}</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="card">
          <div className="card-title">On-Shelf Stock — sizes down, colours across</div>
          {loading ? <p style={{ color: '#93827a' }}>Loading...</p> : (
            <div style={{ overflowX: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Size</th>
                    {COLORS.map(c => <th key={c} style={{ textAlign: 'center' }}>{c}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {SIZES.map(size => (
                    <tr key={size}>
                      <td style={{ fontWeight: 700, color: '#2e1f23' }}>{size}</td>
                      {COLORS.map(color => {
                        const qty = getQty(color, size);
                        return (
                          <td key={color} style={{ textAlign: 'center' }}>
                            <span style={{ fontWeight: 600, color: qty <= 0 ? '#b3261e' : '#2e1f23' }}>{qty}</span>
                            <button onClick={() => handleQuickAdjust(color, size, qty)} disabled={adjustingCell === `${color}-${size}`}
                              title={`Add/subtract ${color} ${size}`}
                              style={{ marginLeft: '6px', fontSize: '11px', padding: '1px 6px', background: '#ffffff', color: '#8c3242', border: '1px solid #e6dccc', borderRadius: '5px', cursor: 'pointer' }}>
                              {adjustingCell === `${color}-${size}` ? '…' : '±'}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-title">Set Stock (exact count)</div>
          <form onSubmit={handleUpdate} style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div><div style={{ fontSize: '12px', color: '#93827a', marginBottom: '6px' }}>Colour</div>
              <select value={form.color} onChange={e => setForm({ ...form, color: e.target.value })}>{COLORS.map(c => <option key={c}>{c}</option>)}</select></div>
            <div><div style={{ fontSize: '12px', color: '#93827a', marginBottom: '6px' }}>Size</div>
              <select value={form.size} onChange={e => setForm({ ...form, size: e.target.value })}>{SIZES.map(s => <option key={s}>{s}</option>)}</select></div>
            <div><div style={{ fontSize: '12px', color: '#93827a', marginBottom: '6px' }}>Quantity</div>
              <input type="number" value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} placeholder="e.g. 40" required /></div>
            <button type="submit" className="btn-primary" disabled={updating}>{updating ? 'Saving...' : 'Set Stock'}</button>
          </form>
        </div>
      </>)}

      {/* ---------------- TAB 2: INCOMING ---------------- */}
      {tab === 'incoming' && (<>
        <div className="card">
          <div className="card-title">Log Incoming Stock (ordered & dispatched by manufacturer)</div>
          <form onSubmit={handleAddIncoming} style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div><div style={{ fontSize: '12px', color: '#93827a', marginBottom: '6px' }}>Colour</div>
              <select value={incForm.color} onChange={e => setIncForm({ ...incForm, color: e.target.value })}>{COLORS.map(c => <option key={c}>{c}</option>)}</select></div>
            <div><div style={{ fontSize: '12px', color: '#93827a', marginBottom: '6px' }}>Size</div>
              <select value={incForm.size} onChange={e => setIncForm({ ...incForm, size: e.target.value })}>{SIZES.map(s => <option key={s}>{s}</option>)}</select></div>
            <div><div style={{ fontSize: '12px', color: '#93827a', marginBottom: '6px' }}>Quantity</div>
              <input type="number" value={incForm.quantity} onChange={e => setIncForm({ ...incForm, quantity: e.target.value })} placeholder="e.g. 50" required /></div>
            <div><div style={{ fontSize: '12px', color: '#93827a', marginBottom: '6px' }}>Note (dispatch date / PO)</div>
              <input value={incForm.note} onChange={e => setIncForm({ ...incForm, note: e.target.value })} placeholder="optional" style={{ width: '180px' }} /></div>
            <button type="submit" className="btn-primary" disabled={addingInc}>{addingInc ? 'Adding...' : 'Add Incoming'}</button>
          </form>
        </div>

        <div className="card">
          <div className="card-title">Incoming Shipments — click Received to add to shelf</div>
          <table>
            <thead><tr><th>Colour</th><th>Size</th><th>Qty</th><th>Note</th><th>Logged</th><th>Action</th></tr></thead>
            <tbody>
              {incoming.map(i => (
                <tr key={i._id}>
                  <td style={{ fontWeight: 600 }}>{i.color}</td>
                  <td>{i.size}</td>
                  <td style={{ fontWeight: 700, color: '#8c3242' }}>{i.quantity}</td>
                  <td style={{ color: '#7d6a63', fontSize: '13px' }}>{i.note || '—'}</td>
                  <td style={{ color: '#93827a', fontSize: '12px' }}>{new Date(i.createdAt).toLocaleDateString()}</td>
                  <td>
                    <button className="btn-primary" onClick={() => handleReceive(i._id)} disabled={receivingId === i._id} style={{ fontSize: '12px', padding: '6px 12px', marginRight: '8px' }}>
                      {receivingId === i._id ? '...' : '✓ Received'}
                    </button>
                    <button className="btn-danger" onClick={() => handleDeleteIncoming(i._id)} style={{ fontSize: '12px', padding: '6px 12px' }}>Delete</button>
                  </td>
                </tr>
              ))}
              {incoming.length === 0 && <tr><td colSpan="6" style={{ color: '#93827a', textAlign: 'center' }}>No incoming stock logged</td></tr>}
            </tbody>
          </table>
        </div>
      </>)}

      {/* ---------------- TAB 3: REORDER PLAN ---------------- */}
      {tab === 'reorder' && (
        <div className="card">
          <div className="card-title">Reorder Plan — live velocity per colour + size</div>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '16px' }}>
            <div><div style={{ fontSize: '12px', color: '#93827a', marginBottom: '4px' }}>Demand window (days)</div>
              <input type="number" value={windowInput} onChange={e => setWindowInput(e.target.value)} style={{ width: '90px' }} /></div>
            <div><div style={{ fontSize: '12px', color: '#93827a', marginBottom: '4px' }}>Target cover (days)</div>
              <input type="number" value={coverInput} onChange={e => setCoverInput(e.target.value)} style={{ width: '90px' }} /></div>
            <label style={{ color: '#4a3840', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <input type="checkbox" checked={onlyReorder} onChange={e => setOnlyReorder(e.target.checked)} /> Only show items to reorder
            </label>
          </div>
          <p style={{ fontSize: '12px', color: '#93827a', marginBottom: '14px' }}>
            Velocity = units sold per day over the last {windowDays} days, unique to each colour+size. <b style={{ color: '#7d6a63' }}>Pending</b> = blanks the unmanifested/pending orders will consume once booked (returns cover matching pieces first). <b style={{ color: '#7d6a63' }}>Left after pending</b> = on-shelf minus that. Recommended = enough to cover {coverDays} days, minus the effective (post-pending) shelf and incoming. Screenshot this and send to your manufacturer.
          </p>
          {!analytics ? <p style={{ color: '#93827a' }}>Loading...</p> : (
            <div style={{ overflowX: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>Colour</th><th>Size</th>
                    <th style={{ color: '#7a6a12' }}>Order Qty</th>
                    <th>Velocity /day</th><th>On Shelf</th>
                    <th style={{ color: '#b3261e' }}>Pending</th>
                    <th>Left after pending</th>
                    <th>Incoming</th>
                    <th>Stockout in</th><th>After incoming</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.rows.filter(r => !onlyReorder || r.recommended > 0).map(r => {
                    const eff = r.effectiveShelf != null ? r.effectiveShelf : r.onShelf;
                    const b = stockoutBadge(r.stockoutDays, eff);
                    const b2 = stockoutBadge(r.stockoutAfterIncoming, eff + r.incoming);
                    return (
                      <tr key={`${r.color}-${r.size}`}>
                        <td style={{ fontWeight: 600 }}>{r.color}</td>
                        <td style={{ fontWeight: 600 }}>{r.size}</td>
                        <td style={{ fontWeight: 700, color: r.recommended > 0 ? '#7a6a12' : '#b0a296', fontSize: '15px' }}>{r.recommended || '—'}</td>
                        <td>{r.velocity}</td>
                        <td style={{ color: r.onShelf <= 0 ? '#b3261e' : '#2e1f23' }}>{r.onShelf}</td>
                        <td style={{ color: r.pendingDemand > 0 ? '#b3261e' : '#b0a296' }}>{r.pendingDemand > 0 ? `-${r.pendingDemand}` : '—'}</td>
                        <td style={{ fontWeight: 600, color: eff <= 0 ? '#b3261e' : '#2e1f23' }}>{eff}</td>
                        <td style={{ color: '#8c3242' }}>{r.incoming || '—'}</td>
                        <td><span className={`badge ${b.class}`}>{b.label}</span></td>
                        {/* always shown — with no incoming it equals the plain
                            stockout, so this one column covers every variant */}
                        <td><span className={`badge ${b2.class}`}>{b2.label}</span></td>
                      </tr>
                    );
                  })}
                  {analytics.rows.filter(r => !onlyReorder || r.recommended > 0).length === 0 && (
                    <tr><td colSpan="10" style={{ color: '#2e7d50', textAlign: 'center' }}>Nothing to reorder — all sizes are covered 🎉</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
